import * as React from 'react';
import { makeStyles, tokens, Text, Button } from '@fluentui/react-components';
import { ChevronDownRegular, SignOutRegular } from '@fluentui/react-icons';
import { useIntl } from 'react-intl';
import { useAuth } from '../auth/AuthContext';
import { headerMessages } from '../intl/messages';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke1,
    backgroundColor: tokens.colorNeutralBackground2,
    // Establishes stacking context so the inline dropdown overlays content below
    position: 'relative',
    zIndex: 10,
  },
  title: {
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.02em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  titleWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    minWidth: 0,
    flex: 1,
  },
  eyebrow: {
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase100,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // Wrapper for the trigger button — anchors the dropdown panel
  menuAnchor: {
    position: 'relative',
  },
  // Inline dropdown panel — avoids React Portal so it never escapes the iframe
  menuPanel: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: tokens.spacingVerticalXS,
    minWidth: '180px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderTopStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightWidth: '1px',
    borderRightStyle: 'solid',
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomWidth: '1px',
    borderBottomStyle: 'solid',
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftWidth: '1px',
    borderLeftStyle: 'solid',
    borderLeftColor: tokens.colorNeutralStroke1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    zIndex: 1000,
    overflow: 'hidden',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalM}`,
    cursor: 'pointer',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    ':active': {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
    },
  },
  menuItemIcon: {
    display: 'flex',
    alignItems: 'center',
    width: '20px',
    flexShrink: 0,
    color: tokens.colorNeutralForeground2,
  },
});

export function Header() {
  const styles = useStyles();
  const { logout, userName, workspaceDisplayName } = useAuth();
  const intl = useIntl();

  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuAnchorRef = React.useRef<HTMLDivElement>(null);

  // Close the dropdown when the user clicks outside it
  React.useEffect(() => {
    if (!menuOpen) {
      return;
    }
    const handlePointerDown = (e: PointerEvent) => {
      if (
        menuAnchorRef.current &&
        !menuAnchorRef.current.contains(e.target as Node)
      ) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [menuOpen]);

  // Eyebrow shows the user's display name; falls back to the add-in title.
  const eyebrowText = userName || intl.formatMessage(headerMessages.addInTitle);

  // Title shows the persisted workspace display name; falls back to brand name.
  const titleText =
    workspaceDisplayName || intl.formatMessage(headerMessages.brandName);

  return (
    <div className={styles.header}>
      <div className={styles.titleWrap}>
        <Text className={styles.eyebrow}>{eyebrowText}</Text>
        <Text className={styles.title}>{titleText}</Text>
      </div>

      {/* Inline dropdown — no React Portal, safe inside Office Add-in iframes */}
      <div ref={menuAnchorRef} className={styles.menuAnchor}>
        <Button
          appearance="subtle"
          size="small"
          icon={<ChevronDownRegular />}
          aria-label={intl.formatMessage(headerMessages.accountMenu)}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(open => !open)}
          onKeyDown={e => {
            if (e.key === 'Escape') {
              setMenuOpen(false);
            }
          }}
        />

        {menuOpen && (
          <div
            className={styles.menuPanel}
            role="menu"
            onKeyDown={e => {
              if (e.key === 'Escape') {
                setMenuOpen(false);
              }
            }}
          >
            <div
              className={styles.menuItem}
              role="menuitem"
              tabIndex={0}
              onClick={() => {
                logout();
                setMenuOpen(false);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  logout();
                  setMenuOpen(false);
                }
              }}
            >
              <span className={styles.menuItemIcon}>
                <SignOutRegular />
              </span>
              {intl.formatMessage(headerMessages.signOut)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
